import { LoadingSpinner } from "components/LoadingSpinner";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useParams } from "react-router-dom";
import { useGetPublicCredentialQuery } from "store/queries/training";
import styled from "styled-components";
import { CERTIFICATE_BODY } from "training/components/certificateDesign";
import {
  CertificateCard,
  formatCertificateDate,
} from "training/components/CertificateParts";

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 24px 16px;
`;

/** A plain card, for the two states with no certificate to show. */
const NoticeCard = styled.section`
  width: 100%;
  max-width: 640px;
  padding: 40px 32px;
  border: 1px solid var(--brand-gray-200, #e2e2e4);
  border-radius: 16px;
  background: var(--brand-white, #fff);
  text-align: center;
`;

const Status = styled.div<{ $current: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: ${({ $current }) => ($current ? "4px 12px" : "8px 18px")};
  border: 1px solid
    ${({ $current }) =>
      $current
        ? "var(--green-200, #bbf7d0)"
        : "var(--brand-gray-400, #a5a4ad)"};
  border-radius: 999px;
  font-size: ${({ $current }) => ($current ? "0.85rem" : "1rem")};
  font-weight: var(--font-weight-semibold, 600);
  color: ${({ $current }) =>
    $current ? "var(--green-700, #15803d)" : "var(--brand-gray-600)"};
  background: ${({ $current }) =>
    $current ? "var(--green-50, #f0fdf4)" : "var(--surface-100, #f3f4f6)"};
`;

const Note = styled.p`
  margin: 8px auto 0;
  max-width: 460px;
  color: var(--brand-gray-600);
`;

const NotFoundTitle = styled.h1`
  margin: 0 0 12px;
  font-size: 1.5rem;
`;

/**
 * CLE-3015 — /certified/:credentialId, the public (no login) verification page a Clear Certified
 * user shares, and the certificate URL on their LinkedIn profile. Three states: a current
 * credential, a credential that is no longer current (its holder's training was reset, or their
 * access was removed — the note says neither, so the page does not disclose which), and an
 * unknown id. It shows only what the public endpoint returns (name, hospital, date, version, status)
 * and the credential id already in the URL, on the same certificate as /training/certificate
 * (CertificateParts.tsx) — grayed out, with no date, version or "is Clear Certified", when not current.
 *
 * A link preview (Open Graph tags) needs server-rendered HTML, which this page cannot give; see
 * "Sharing a certificate" in brain/wiki/pages/features/CLEAR-CERTIFIED-TRAINING.md.
 */
export function PublicCertifiedPage() {
  const { credentialId = "" } = useParams();
  const { data, isLoading, error } = useGetPublicCredentialQuery(credentialId, {
    skip: !credentialId,
  });
  const credential = data?.data;
  const notFound =
    !credentialId ||
    (!!error && "status" in error && error.status === 404) ||
    (!isLoading && !error && !credential);

  let title = "Clear Certified";
  if (credential) title = `${credential.name} · Clear Certified`;
  else if (notFound) title = "Credential not found · Clear Certified";
  useDocumentTitle(title);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (notFound || !credential) {
    return (
      <Wrapper>
        <NoticeCard aria-label="Credential not found">
          <NotFoundTitle>
            {notFound
              ? "We could not find this credential"
              : "This credential could not be checked right now"}
          </NotFoundTitle>
          <Note>
            {notFound
              ? "Check that the link was copied in full. ClearHealth credential links end in a 12-character code."
              : "Please reload the page in a moment."}
          </Note>
        </NoticeCard>
      </Wrapper>
    );
  }

  const current = credential.status === "current";

  return (
    <Wrapper>
      <CertificateCard
        ariaLabel="Clear Certified credential"
        testIdPrefix="credential"
        eyebrow="Verified credential"
        certifies="Issued by ClearHealth to"
        // A credential that is not current shows no date, no version and no "is Clear Certified" sentence,
        // and the whole certificate turns gray.
        body={current ? CERTIFICATE_BODY : null}
        muted={!current}
        name={credential.name}
        hospitalName={credential.hospitalName}
        date={
          current && credential.certifiedAt
            ? formatCertificateDate(credential.certifiedAt)
            : null
        }
        credentialId={credentialId.toUpperCase()}
        curriculumVersion={current ? credential.curriculumVersion : null}
      >
        <Status $current={current} data-testid="credential-status">
          <i
            className={current ? "pi pi-check-circle" : "pi pi-info-circle"}
            aria-hidden
          />
          {current ? "Current" : "Not current"}
        </Status>
        {current ? null : (
          <Note>This person is not currently Clear Certified.</Note>
        )}
      </CertificateCard>
    </Wrapper>
  );
}
